/**
 * Admin API Providers Page
 * Manage multiple API keys per provider with load balancing
 * Cursor-style infrastructure for AI model routing
 */

import { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Edit2,
  RefreshCw,
  Key,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Zap,
  Shield,
  Eye,
  EyeOff,
  Copy,
  MoreVertical,
  Activity,
  TrendingUp,
  Server,
  Bot,
  Brain,
  Sparkles,
  Cpu,
  Cloud,
  FileJson
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAIModelsStore } from '@/services/aiModelsService';

// Types
type AIProvider = 'openai' | 'anthropic' | 'google' | 'xai' | 'ollama';
type LoadBalanceStrategy = 'round-robin' | 'least-used' | 'weighted' | 'failover' | 'random';
type KeyStatus = 'active' | 'rate-limited' | 'error' | 'disabled';

interface APIKeyConfig {
  id: string;
  key: string;
  name: string;
  provider: AIProvider;
  priority: number;
  weight: number;
  status: KeyStatus;
  rateLimit: {
    requestsPerMinute: number;
    tokensPerMinute: number;
  };
  usage: {
    requestsThisMinute: number;
    tokensThisMinute: number;
    lastRequestTime: number;
    totalRequests: number;
    totalTokens: number;
    lastError?: string;
    lastErrorTime?: number;
  };
  createdAt: Date;
  updatedAt: Date;
}

interface ProviderConfig {
  provider: AIProvider;
  keys: APIKeyConfig[];
  strategy: LoadBalanceStrategy;
  enabled: boolean;
  baseUrl?: string;
  defaultModel?: string;
}

// Provider info
const PROVIDER_INFO: Record<AIProvider, { name: string; color: string; icon: React.ReactNode }> = {
  openai: { name: 'OpenAI', color: 'bg-green-500', icon: <Bot size={20} className="text-green-500" /> },
  anthropic: { name: 'Anthropic', color: 'bg-orange-500', icon: <Brain size={20} className="text-orange-500" /> },
  google: { name: 'Google AI', color: 'bg-blue-500', icon: <Sparkles size={20} className="text-blue-500" /> },
  xai: { name: 'xAI (Grok)', color: 'bg-purple-500', icon: <Zap size={20} className="text-purple-500" /> },
  ollama: { name: 'Ollama (Local)', color: 'bg-gray-500', icon: <Server size={20} className="text-gray-500" /> },
};

const STRATEGY_INFO: Record<LoadBalanceStrategy, { name: string; description: string }> = {
  'round-robin': { name: 'Round Robin', description: 'Rotate through keys sequentially' },
  'least-used': { name: 'Least Used', description: 'Use the key with lowest recent usage' },
  'weighted': { name: 'Weighted', description: 'Distribute based on priority weights' },
  'failover': { name: 'Failover', description: 'Use primary, fallback to secondary on failure' },
  'random': { name: 'Random', description: 'Random selection for load distribution' },
};

// Storage key
const STORAGE_KEY = 'ochtopus-api-providers-admin';

export function AdminAPIProviders() {
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<AIProvider | null>(null);
  const [isAddingKey, setIsAddingKey] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [testingKey, setTestingKey] = useState<string | null>(null);

  // New key form state
  const [newKeyForm, setNewKeyForm] = useState({
    name: '',
    key: '',
    priority: 1,
    weight: 50,
    rpm: 500,
    tpm: 200000,
  });

  // Load from storage
  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = () => {
    setIsLoading(true);
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        setProviders(parsed.providers || []);
      } else {
        // Initialize with empty providers
        const initialProviders: ProviderConfig[] = [
          { provider: 'openai', keys: [], strategy: 'round-robin', enabled: false },
          { provider: 'anthropic', keys: [], strategy: 'round-robin', enabled: false },
          { provider: 'google', keys: [], strategy: 'round-robin', enabled: false },
          { provider: 'xai', keys: [], strategy: 'round-robin', enabled: false },
          { provider: 'ollama', keys: [], strategy: 'round-robin', enabled: false },
        ];
        setProviders(initialProviders);
        saveProviders(initialProviders);
      }
    } catch (error) {
      console.error('Failed to load providers:', error);
    }
    setIsLoading(false);
  };

  const saveProviders = (data: ProviderConfig[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ providers: data, savedAt: new Date().toISOString() }));
  };

  const getProvider = (provider: AIProvider): ProviderConfig | undefined => {
    return providers.find(p => p.provider === provider);
  };

  const updateProvider = (provider: AIProvider, updates: Partial<ProviderConfig>) => {
    const newProviders = providers.map(p =>
      p.provider === provider ? { ...p, ...updates } : p
    );
    setProviders(newProviders);
    saveProviders(newProviders);
  };

  const addKey = (provider: AIProvider) => {
    const config = getProvider(provider);
    if (!config) return;

    const newKey: APIKeyConfig = {
      id: crypto.randomUUID(),
      key: newKeyForm.key,
      name: newKeyForm.name || `Key ${config.keys.length + 1}`,
      provider,
      priority: newKeyForm.priority,
      weight: newKeyForm.weight,
      status: 'active',
      rateLimit: {
        requestsPerMinute: newKeyForm.rpm,
        tokensPerMinute: newKeyForm.tpm,
      },
      usage: {
        requestsThisMinute: 0,
        tokensThisMinute: 0,
        lastRequestTime: 0,
        totalRequests: 0,
        totalTokens: 0,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const newKeys = [...config.keys, newKey];
    updateProvider(provider, { keys: newKeys, enabled: true });
    setIsAddingKey(false);
    setNewKeyForm({ name: '', key: '', priority: 1, weight: 50, rpm: 500, tpm: 200000 });
  };

  const removeKey = (provider: AIProvider, keyId: string) => {
    const config = getProvider(provider);
    if (!config) return;

    const newKeys = config.keys.filter(k => k.id !== keyId);
    updateProvider(provider, { keys: newKeys, enabled: newKeys.length > 0 });
  };

  const updateKeyStatus = (provider: AIProvider, keyId: string, status: KeyStatus) => {
    const config = getProvider(provider);
    if (!config) return;

    const newKeys = config.keys.map(k =>
      k.id === keyId ? { ...k, status, updatedAt: new Date() } : k
    );
    updateProvider(provider, { keys: newKeys });
  };

  const testKey = async (provider: AIProvider, keyId: string) => {
    setTestingKey(keyId);
    const config = getProvider(provider);
    const key = config?.keys.find(k => k.id === keyId);
    if (!key) return;

    try {
      // Simulate API test (in real app, this would call the backend)
      await new Promise(resolve => setTimeout(resolve, 1500));

      // For demo, randomly succeed/fail
      const success = Math.random() > 0.3;

      if (success) {
        updateKeyStatus(provider, keyId, 'active');
      } else {
        const config = getProvider(provider);
        if (config) {
          const newKeys = config.keys.map(k =>
            k.id === keyId
              ? { ...k, status: 'error' as KeyStatus, usage: { ...k.usage, lastError: 'Invalid API key', lastErrorTime: Date.now() } }
              : k
          );
          updateProvider(provider, { keys: newKeys });
        }
      }
    } catch (error) {
      updateKeyStatus(provider, keyId, 'error');
    }
    setTestingKey(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const maskKey = (key: string) => {
    if (key.length <= 8) return '••••••••';
    return key.substring(0, 4) + '••••••••••••' + key.substring(key.length - 4);
  };

  const getStatusColor = (status: KeyStatus) => {
    switch (status) {
      case 'active': return 'text-green-500 bg-green-500/10';
      case 'rate-limited': return 'text-yellow-500 bg-yellow-500/10';
      case 'error': return 'text-red-500 bg-red-500/10';
      case 'disabled': return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getStatusIcon = (status: KeyStatus) => {
    switch (status) {
      case 'active': return <CheckCircle2 size={14} />;
      case 'rate-limited': return <Clock size={14} />;
      case 'error': return <XCircle size={14} />;
      case 'disabled': return <AlertTriangle size={14} />;
    }
  };

  // Calculate stats
  const totalKeys = providers.reduce((sum, p) => sum + p.keys.length, 0);
  const activeKeys = providers.reduce((sum, p) => sum + p.keys.filter(k => k.status === 'active').length, 0);
  const enabledProviders = providers.filter(p => p.enabled).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">API Providers</h1>
          <p className="text-text-secondary">Manage API keys for AI model providers with load balancing</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const json = useAIModelsStore.getState().exportToJSON();
              const blob = new Blob([json], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `ochtopus-ai-models-${new Date().toISOString().split('T')[0]}.json`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
            className="btn btn-secondary"
            title="Export AI Models as JSON"
          >
            <FileJson size={16} />
            Export
          </button>
          <button
            onClick={() => {
              const json = useAIModelsStore.getState().exportToJSON();
              localStorage.setItem('ochtopus-ai-models-api', json);
              alert('AI Models synced to IDE API. Models will be available in the application.');
            }}
            className="btn bg-green-500/10 text-green-500 hover:bg-green-500/20"
            title="Sync AI Models to IDE"
          >
            <Cloud size={16} />
            Sync to IDE
          </button>
          <button
            onClick={loadProviders}
            className="btn btn-secondary"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent/10">
              <Server className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">{enabledProviders}</p>
              <p className="text-sm text-text-secondary">Enabled Providers</p>
            </div>
          </div>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <Key className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">{activeKeys}/{totalKeys}</p>
              <p className="text-sm text-text-secondary">Active API Keys</p>
            </div>
          </div>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Activity className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">
                {providers.reduce((sum, p) => sum + p.keys.reduce((s, k) => s + k.usage.totalRequests, 0), 0).toLocaleString()}
              </p>
              <p className="text-sm text-text-secondary">Total Requests</p>
            </div>
          </div>
        </div>
      </div>

      {/* Provider Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {providers.map((provider) => {
          const info = PROVIDER_INFO[provider.provider];
          const isSelected = selectedProvider === provider.provider;

          return (
            <div
              key={provider.provider}
              className={cn(
                'bg-bg-card border rounded-xl overflow-hidden transition-colors',
                isSelected ? 'border-accent' : 'border-border-default'
              )}
            >
              {/* Provider Header */}
              <div className="p-4 border-b border-border-default">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center text-xl', info.color + '/20')}>
                      {info.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-primary">{info.name}</h3>
                      <p className="text-xs text-text-tertiary">
                        {provider.keys.length} key{provider.keys.length !== 1 ? 's' : ''} configured
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={provider.enabled}
                        onChange={(e) => updateProvider(provider.provider, { enabled: e.target.checked })}
                        className="sr-only peer"
                        disabled={provider.keys.length === 0}
                      />
                      <div className={cn(
                        "w-11 h-6 rounded-full peer transition-colors",
                        "peer-checked:after:translate-x-full peer-checked:after:border-white",
                        "after:content-[''] after:absolute after:top-[2px] after:left-[2px]",
                        "after:bg-white after:border-gray-300 after:border after:rounded-full",
                        "after:h-5 after:w-5 after:transition-all",
                        provider.enabled ? 'bg-accent' : 'bg-bg-elevated'
                      )} />
                    </label>
                  </div>
                </div>

                {/* Strategy Selector */}
                <div className="mt-3">
                  <label className="text-xs text-text-tertiary mb-1 block">Load Balancing Strategy</label>
                  <select
                    value={provider.strategy}
                    onChange={(e) => updateProvider(provider.provider, { strategy: e.target.value as LoadBalanceStrategy })}
                    className="w-full px-3 py-1.5 text-sm rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  >
                    {Object.entries(STRATEGY_INFO).map(([key, value]) => (
                      <option key={key} value={key}>{value.name} - {value.description}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Keys List */}
              <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
                {provider.keys.length === 0 ? (
                  <div className="text-center py-6 text-text-tertiary">
                    <Key className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No API keys configured</p>
                  </div>
                ) : (
                  provider.keys.map((key) => (
                    <div
                      key={key.id}
                      className="bg-bg-elevated rounded-lg p-3 border border-border-default"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-text-primary text-sm">{key.name}</span>
                            <span className={cn('px-2 py-0.5 rounded-full text-xs flex items-center gap-1', getStatusColor(key.status))}>
                              {getStatusIcon(key.status)}
                              {key.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="text-xs text-text-tertiary font-mono">
                              {showKey[key.id] ? key.key : maskKey(key.key)}
                            </code>
                            <button
                              onClick={() => setShowKey(prev => ({ ...prev, [key.id]: !prev[key.id] }))}
                              className="p-1 hover:bg-bg-base rounded text-text-tertiary"
                            >
                              {showKey[key.id] ? <EyeOff size={12} /> : <Eye size={12} />}
                            </button>
                            <button
                              onClick={() => copyToClipboard(key.key)}
                              className="p-1 hover:bg-bg-base rounded text-text-tertiary"
                            >
                              <Copy size={12} />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => testKey(provider.provider, key.id)}
                            disabled={testingKey === key.id}
                            className={cn(
                              "p-1.5 rounded hover:bg-bg-base text-text-tertiary transition-colors",
                              testingKey === key.id && "animate-pulse"
                            )}
                            title="Test key"
                          >
                            {testingKey === key.id ? (
                              <RefreshCw size={14} className="animate-spin" />
                            ) : (
                              <Zap size={14} />
                            )}
                          </button>
                          <button
                            onClick={() => removeKey(provider.provider, key.id)}
                            className="p-1.5 rounded hover:bg-red-500/10 text-red-500 transition-colors"
                            title="Remove key"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Key Stats */}
                      <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                        <div>
                          <span className="text-text-tertiary">Priority:</span>
                          <span className="text-text-primary ml-1">{key.priority}</span>
                        </div>
                        <div>
                          <span className="text-text-tertiary">Weight:</span>
                          <span className="text-text-primary ml-1">{key.weight}%</span>
                        </div>
                        <div>
                          <span className="text-text-tertiary">Requests:</span>
                          <span className="text-text-primary ml-1">{key.usage.totalRequests.toLocaleString()}</span>
                        </div>
                      </div>

                      {/* Rate Limit Bar */}
                      <div className="mt-2">
                        <div className="flex justify-between text-xs text-text-tertiary mb-1">
                          <span>Rate Limit</span>
                          <span>{key.usage.requestsThisMinute}/{key.rateLimit.requestsPerMinute} RPM</span>
                        </div>
                        <div className="h-1.5 bg-bg-base rounded-full overflow-hidden">
                          <div
                            className={cn(
                              "h-full transition-all",
                              key.usage.requestsThisMinute / key.rateLimit.requestsPerMinute > 0.9
                                ? 'bg-red-500'
                                : key.usage.requestsThisMinute / key.rateLimit.requestsPerMinute > 0.7
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                            )}
                            style={{ width: `${Math.min(100, (key.usage.requestsThisMinute / key.rateLimit.requestsPerMinute) * 100)}%` }}
                          />
                        </div>
                      </div>

                      {/* Error Info */}
                      {key.usage.lastError && (
                        <div className="mt-2 p-2 bg-red-500/10 rounded text-xs text-red-400">
                          <div className="flex items-center gap-1">
                            <AlertTriangle size={12} />
                            <span>{key.usage.lastError}</span>
                          </div>
                          {key.usage.lastErrorTime && (
                            <span className="text-red-400/70">
                              {new Date(key.usage.lastErrorTime).toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}

                {/* Add Key Button */}
                {isAddingKey && selectedProvider === provider.provider ? (
                  <div className="bg-bg-elevated rounded-lg p-4 border border-accent">
                    <h4 className="font-medium text-text-primary mb-3">Add New API Key</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-text-tertiary mb-1 block">Key Name</label>
                        <input
                          type="text"
                          value={newKeyForm.name}
                          onChange={(e) => setNewKeyForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Production Key 1"
                          className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-text-tertiary mb-1 block">API Key</label>
                        <input
                          type="password"
                          value={newKeyForm.key}
                          onChange={(e) => setNewKeyForm(prev => ({ ...prev, key: e.target.value }))}
                          placeholder="sk-..."
                          className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none font-mono"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-text-tertiary mb-1 block">Priority (1-10)</label>
                          <input
                            type="number"
                            min={1}
                            max={10}
                            value={newKeyForm.priority}
                            onChange={(e) => setNewKeyForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 1 }))}
                            className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-text-tertiary mb-1 block">Weight (%)</label>
                          <input
                            type="number"
                            min={1}
                            max={100}
                            value={newKeyForm.weight}
                            onChange={(e) => setNewKeyForm(prev => ({ ...prev, weight: parseInt(e.target.value) || 50 }))}
                            className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-text-tertiary mb-1 block">RPM Limit</label>
                          <input
                            type="number"
                            value={newKeyForm.rpm}
                            onChange={(e) => setNewKeyForm(prev => ({ ...prev, rpm: parseInt(e.target.value) || 500 }))}
                            className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-text-tertiary mb-1 block">TPM Limit</label>
                          <input
                            type="number"
                            value={newKeyForm.tpm}
                            onChange={(e) => setNewKeyForm(prev => ({ ...prev, tpm: parseInt(e.target.value) || 200000 }))}
                            className="w-full px-3 py-2 text-sm rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => addKey(provider.provider)}
                          disabled={!newKeyForm.key}
                          className="btn btn-primary btn-sm flex-1"
                        >
                          <Plus size={14} />
                          Add Key
                        </button>
                        <button
                          onClick={() => {
                            setIsAddingKey(false);
                            setSelectedProvider(null);
                          }}
                          className="btn btn-secondary btn-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setSelectedProvider(provider.provider);
                      setIsAddingKey(true);
                    }}
                    className="w-full py-3 border-2 border-dashed border-border-default rounded-lg text-text-tertiary hover:border-accent hover:text-accent transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus size={16} />
                    Add API Key
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Banner */}
      <div className="bg-accent/10 border border-accent/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-accent">Security Notice</h4>
            <p className="text-sm text-text-secondary mt-1">
              API keys are stored locally in this demo. In production, keys should be encrypted and stored securely on the server.
              Never expose API keys to the client side.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
